import { useState, useMemo } from "react";
import { useFamily } from "@/hooks/useFamily";
import { useAllChildren } from "@/hooks/useAllChildren";
import { useAuth } from "@/hooks/useAuth";
import { Navigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ExchangeSection } from "@/components/ExchangeSection";
import { HandoverDetailsSheet } from "@/components/HandoverDetailsSheet";
import { getDateString, formatDate } from "@/lib/family-date";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";



// ── Oslo date helpers ──────────────────────────────────
function parseDateString(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/** Get Monday of the week containing `date` (Oslo local) */
function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

/** ISO week number */
function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

const DAY_LABELS = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];

// ── Types ──────────────────────────────────────────────
interface Child {
  id: string;
  name: string;
  avatar_emoji: string;
}

interface FamilyMember {
  user_id: string;
  display_name: string | null;
  role: string;
}

interface CustodyDay {
  id: string;
  child_id: string;
  date: string;
  custodial_parent_user_id: string;
  is_handover: boolean;
  handover_type: string | null;
  handover_time: string | null;
  handover_note: string | null;
}

interface ChildCoparent {
  child_id: string;
  coparent_user_id: string;
}

// ── Main page ──────────────────────────────────────────
export default function Custody() {
  const { timezone, memberships } = useFamily();
  const { children: allChildren, allCoparents, splitChildIds, hasAnySplitChild } = useAllChildren();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  

  // Only show children that have a co-parent
  const children = allChildren.filter((c) => splitChildIds.has(c.id));

  const [activeChildId, setActiveChildId] = useState<string>(children[0]?.id ?? "");

  const [handoverTarget, setHandoverTarget] = useState<{
    childId: string;
    dateStr: string;
    day: CustodyDay | null;
  } | null>(null);

  // Date window: 1 week back, 3 weeks ahead from Monday of current week
  const today = new Date();
  const todayStr = getDateString(today, timezone);
  const thisMonday = getMonday(today);
  const rangeStartDate = addDays(thisMonday, -7);
  const rangeEndDate = addDays(thisMonday, 27);
  const startDateStr = getDateString(rangeStartDate, timezone);
  const endDateStr = getDateString(rangeEndDate, timezone);

  // Weeks grid
  const weeks = useMemo(() => {
    const result: { monday: Date; weekNum: number; days: Date[] }[] = [];
    let current = new Date(rangeStartDate);
    for (let w = 0; w < 4; w++) {
      const monday = new Date(current);
      const days: Date[] = [];
      for (let d = 0; d < 7; d++) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      result.push({ monday, weekNum: getWeekNumber(monday), days });
    }
    return result;
  }, [startDateStr]);

  // All family IDs the user is in
  const familyIds = memberships.map((m) => m.family_id);

  // ── Data fetching ────────────────────────────────────
  // Fetch family members from all families for display names
  const { data: members = [] } = useQuery({
    queryKey: ["family-members-all", familyIds.join(",")],
    queryFn: async () => {
      if (familyIds.length === 0) return [];
      const { data, error } = await supabase
        .from("family_members")
        .select("user_id, display_name, role, family_id")
        .in("family_id", familyIds);
      if (error) throw error;
      return data as (FamilyMember & { family_id: string })[];
    },
    enabled: familyIds.length > 0,
  });

  const childCoparents = allCoparents as ChildCoparent[];

  const queryKey = ["custody-days", familyIds.join(","), startDateStr, endDateStr];

  const { data: custodyDays = [] } = useQuery({
    queryKey,
    queryFn: async () => {
      if (familyIds.length === 0) return [];
      const { data, error } = await supabase
        .from("custody_days")
        .select("*")
        .in("family_id", familyIds)
        .gte("date", startDateStr)
        .lte("date", endDateStr);
      if (error) throw error;
      return (data ?? []) as CustodyDay[];
    },
    enabled: familyIds.length > 0,
  });

  // Index by child_id + date
  const dayMap = useMemo(() => {
    const m = new Map<string, CustodyDay>();
    for (const d of custodyDays) {
      m.set(`${d.child_id}:${d.date}`, d);
    }
    return m;
  }, [custodyDays]);

  // Map child_id → coparent_user_id
  const coparentByChild = useMemo(() => {
    const m = new Map<string, string>();
    for (const cc of childCoparents) {
      m.set(cc.child_id, cc.coparent_user_id);
    }
    return m;
  }, [childCoparents]);

  const parents = members.filter((m) => m.role === "parent");

  // Sjekk om noen barn har en satt medforelder, eller om familien har minst 2 foreldre
  const familyHasOtherParent = parents.some((m) => m.user_id !== user?.id);

  function getParentLabel(userId: string) {
    if (userId === user?.id) return "Deg";
    const m = members.find((m) => m.user_id === userId);
    return m?.display_name || "Medforelder";
  }

  function getCoparentForChild(childId: string): FamilyMember | undefined {
    const coparentId = coparentByChild.get(childId);
    if (coparentId) {
      return members.find((m) => m.user_id === coparentId);
    }
    // Fallback: bruk global annen forelder
    return parents.find((m) => m.user_id !== user?.id);
  }

  // ── Toggle mutation ──────────────────────────────────
  const toggleMutation = useMutation({
    mutationFn: async ({
      childId,
      dateStr,
    }: {
      childId: string;
      dateStr: string;
    }) => {
      const { data, error } = await supabase.rpc("toggle_custody_day", {
        _child_id: childId,
        _date: dateStr,
      });
      if (error) throw error;
      return data;
    },
    onMutate: async ({ childId, dateStr }) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<CustodyDay[]>(queryKey);

      queryClient.setQueryData<CustodyDay[]>(queryKey, (old = []) => {
        const existing = old.find(
          (d) => d.child_id === childId && d.date === dateStr
        );
        const otherParent = getCoparentForChild(childId);
        if (existing) {
          const newParent =
            existing.custodial_parent_user_id === user?.id
              ? otherParent?.user_id || user?.id
              : user?.id;
          return old.map((d) =>
            d.child_id === childId && d.date === dateStr
              ? { ...d, custodial_parent_user_id: newParent! }
              : d
          );
        }
        return [
          ...old,
          {
            id: crypto.randomUUID(),
            child_id: childId,
            date: dateStr,
            custodial_parent_user_id: user!.id,
            is_handover: false,
            handover_type: null,
            handover_time: null,
            handover_note: null,
          } as CustodyDay,
        ];
      });

      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
      toast.error("Kunne ikke oppdatere");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["current-custody", familyIds.join(",")] });
    },
  });

  // ── Handover mutation ────────────────────────────────
  const handoverMutation = useMutation({
    mutationFn: async (vars: {
      childId: string;
      dateStr: string;
      is_handover: boolean;
      handover_type: string | null;
      handover_time: string | null;
      handover_note: string | null;
    }) => {
      const { data, error } = await supabase.rpc("set_handover_details", {
        _child_id: vars.childId,
        _date: vars.dateStr,
        _is_handover: vars.is_handover,
        _handover_type: vars.handover_type,
        _handover_time: vars.handover_time,
        _handover_note: vars.handover_note,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setHandoverTarget(null);
      toast.success("Overlevering oppdatert");
    },
    onError: () => {
      toast.error("Kunne ikke lagre overlevering");
    },
  });

  // Guard: need at least one child with a co-parent
  if (!hasAnySplitChild) {
    return <Navigate to="/dashboard" replace />;
  }

  // Remove the old home_mode guard — split features are now driven by child_coparents

  return (
    <div className="min-h-full bg-background px-4 pt-safe pb-6">
      <div className="mx-auto max-w-sm space-y-6">
        <h1 className="text-center font-heading text-lg font-semibold tracking-tight text-foreground">Samarbeid</h1>

        {children.length === 0 && (
          <div className="text-center py-12 space-y-3">
            <p className="text-muted-foreground">Ingen barn lagt til ennå.</p>
            <Link
              to="/settings"
              className="text-sm text-primary underline underline-offset-2"
            >
              Legg til barn i Innstillinger
            </Link>
          </div>
        )}

        {!familyHasOtherParent && children.length > 0 && (
          <div className="rounded-xl border border-border bg-muted/50 p-3 text-sm text-muted-foreground">
            Bare én forelder registrert.{" "}
            <Link
              to="/settings"
              className="text-primary underline underline-offset-2"
            >
              Inviter medforelder
            </Link>{" "}
            for å starte.
          </div>
        )}

        {children.length > 0 && (
          <Tabs value={activeChildId || children[0]?.id} onValueChange={setActiveChildId}>
            <TabsList className="w-full">
              {children.map((child) => (
                <TabsTrigger key={child.id} value={child.id} className="flex-1 gap-1.5">
                  <span>{child.avatar_emoji}</span>
                  <span>{child.name}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {children.map((child) => {
              const childCoparent = getCoparentForChild(child.id);
              const hasCoparent = !!childCoparent;
              return (
                <TabsContent key={child.id} value={child.id}>
                  <ChildGrid
                    child={child}
                    weeks={weeks}
                    todayStr={todayStr}
                    dayMap={dayMap}
                    currentUserId={user!.id}
                    getParentLabel={getParentLabel}
                    hasOtherParent={hasCoparent}
                    coparent={childCoparent}
                    timezone={timezone}
                    onToggle={(dateStr) =>
                      toggleMutation.mutate({ childId: child.id, dateStr })
                    }
                    onHandoverTap={(dateStr) => {
                      const day = dayMap.get(`${child.id}:${dateStr}`) ?? null;
                      setHandoverTarget({ childId: child.id, dateStr, day });
                    }}
                  />
                </TabsContent>
              );
            })}
          </Tabs>
        )}

        {/* Bytte & Overlevering (formerly standalone Bytte tab) */}
        {children.length > 0 && (
          <section className="space-y-3 pt-4 border-t border-border">
            <h2 className="text-base font-semibold tracking-tight text-foreground">Bytte & Overlevering</h2>
            <ExchangeSection embeddedInSamarbeid selectedChildId={activeChildId || children[0]?.id} />
          </section>
        )}
      </div>

      {handoverTarget && (
        <HandoverDetailsSheet
          open
          onClose={() => setHandoverTarget(null)}
          dateLabel={formatDate(parseDateString(handoverTarget.dateStr), timezone, {
            weekday: "long",
            day: "numeric",
            month: "long",
          })}
          isHandover={handoverTarget.day?.is_handover ?? false}
          handoverType={handoverTarget.day?.handover_type ?? null}
          handoverTime={handoverTarget.day?.handover_time ?? null}
          handoverNote={handoverTarget.day?.handover_note ?? null}
          saving={handoverMutation.isPending}
          onSave={(details) =>
            handoverMutation.mutate({
              childId: handoverTarget.childId,
              dateStr: handoverTarget.dateStr,
              ...details,
            })
          }
        />
      )}

    </div>
  );
}

// ── Child grid component ───────────────────────────────
function ChildGrid({
  child,
  weeks,
  todayStr,
  dayMap,
  currentUserId,
  getParentLabel,
  hasOtherParent,
  coparent,
  timezone,
  onToggle,
  onHandoverTap,
}: {
  child: Child;
  weeks: { monday: Date; weekNum: number; days: Date[] }[];
  todayStr: string;
  dayMap: Map<string, CustodyDay>;
  currentUserId: string;
  getParentLabel: (uid: string) => string;
  hasOtherParent: boolean;
  coparent: FamilyMember | undefined;
  timezone: string;
  onToggle: (dateStr: string) => void;
  onHandoverTap: (dateStr: string) => void;
}) {
  return (
    <div className="space-y-2">

      {/* Day header row */}
      <div className="grid grid-cols-[3rem_repeat(7,1fr)] gap-0.5 text-center">
        <div />
        {DAY_LABELS.map((d) => (
          <div key={d} className="text-[10px] font-medium text-muted-foreground py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Week rows */}
      {weeks.map((week) => (
        <div
          key={week.weekNum + "-" + getDateString(week.monday, timezone)}
          className="grid grid-cols-[3rem_repeat(7,1fr)] gap-0.5"
        >
          <div className="flex items-center justify-center text-[10px] text-muted-foreground font-medium">
            Uke&nbsp;{week.weekNum}
          </div>
          {week.days.map((dayDate) => {
            const dateStr = getDateString(dayDate, timezone);
            const isToday = dateStr === todayStr;
            const day = dayMap.get(`${child.id}:${dateStr}`);
            const isMe = day?.custodial_parent_user_id === currentUserId;
            const label = day ? getParentLabel(day.custodial_parent_user_id) : null;

            return (
              <button
                key={dateStr}
                onClick={() => hasOtherParent && onToggle(dateStr)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  onHandoverTap(dateStr);
                }}
                disabled={!hasOtherParent}
                className={cn(
                  "relative flex flex-col items-center justify-center rounded-lg py-2 px-0.5 transition-all active:scale-95 min-h-[3rem] select-none",
                  day
                    ? isMe
                      ? "bg-primary/15 text-primary"
                      : "bg-secondary text-muted-foreground"
                    : "bg-muted/40 text-muted-foreground",
                  isToday && "ring-2 ring-primary/50",
                  !hasOtherParent && "opacity-60"
                )}
              >
                <span className="text-[10px] leading-none font-medium">
                  {dayDate.getDate()}
                </span>
                {label && (
                  <span className="text-[8px] leading-tight mt-0.5 truncate max-w-full">
                    {label}
                  </span>
                )}
                {day?.is_handover && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onHandoverTap(dateStr);
                    }}
                    className="absolute top-0.5 right-0.5 text-[10px] leading-none"
                    aria-label="Overleveringsdetaljer"
                  >
                    🔁
                  </button>
                )}
              </button>
            );
          })}
        </div>
      ))}

      {/* Tap hint for handover */}
      <p className="text-[10px] text-muted-foreground text-center">
        Hold inne for detaljer om byttedag.
      </p>
    </div>
  );
}
